import {
    collection,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    getDocs,
    getDoc,
    query,
    orderBy,
    serverTimestamp,
    Timestamp,
} from 'firebase/firestore';
import { db } from './config';
import { uploadBase64ImageToCloudinary } from '../services/cloudinaryService';

// Starter Template data interface
export interface StarterTemplate {
    id?: string;
    title: string;
    description: string;
    longDescription: string;
    type: 'template' | 'extension' | 'tool' | 'boilerplate'; // Type of resource
    category: string; // e.g., 'Flutter', 'React', 'Node.js', 'VS Code Extension'
    icon?: string;
    coverImage?: string;
    screenshots?: string[];
    features: string[];
    technologies: string[];
    tags: string[];
    githubUrl?: string;
    downloadUrl?: string;
    demoUrl?: string;
    documentationUrl?: string;
    installation: string; // Installation instructions
    usage: string; // Usage instructions
    changelog?: string;
    version?: string;
    license?: string;
    author: string;
    contributors?: string[];
    downloads?: number;
    stars?: number;
    forks?: number;
    issues?: number;
    lastUpdated?: string;
    requirements: string[]; // System requirements or dependencies
    benefits: string[]; // What benefits it provides to developers
    useCases: string[]; // When to use this template/tool
    featured?: boolean;
    order?: number;
    createdAt?: Timestamp;
    updatedAt?: Timestamp;
}

// Collection reference
const starterTemplatesCollection = collection(db, 'starterTemplates');

/**
 * Get all starter templates
 */
export const getAllStarterTemplates = async (): Promise<StarterTemplate[]> => {
    try {
        const templatesQuery = query(starterTemplatesCollection, orderBy('order', 'asc'));
        const querySnapshot = await getDocs(templatesQuery);

        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        } as StarterTemplate));
    } catch (error) {
        console.error('Error getting starter templates:', error);
        throw error;
    }
};

/**
 * Get featured starter templates
 */
export const getFeaturedStarterTemplates = async (): Promise<StarterTemplate[]> => {
    try {
        const templates = await getAllStarterTemplates();
        return templates.filter(template => template.featured);
    } catch (error) {
        console.error('Error getting featured starter templates:', error);
        throw error;
    }
};

/**
 * Get starter templates by category
 */
export const getStarterTemplatesByCategory = async (category: string): Promise<StarterTemplate[]> => {
    try {
        const templates = await getAllStarterTemplates();
        return templates.filter(template => template.category.toLowerCase() === category.toLowerCase());
    } catch (error) {
        console.error('Error getting starter templates by category:', error);
        throw error;
    }
};

/**
 * Get starter templates by type
 */
export const getStarterTemplatesByType = async (type: 'template' | 'extension' | 'tool' | 'boilerplate'): Promise<StarterTemplate[]> => {
    try {
        const templates = await getAllStarterTemplates();
        return templates.filter(template => template.type === type);
    } catch (error) {
        console.error('Error getting starter templates by type:', error);
        throw error;
    }
};

/**
 * Get starter template by ID
 */
export const getStarterTemplateById = async (id: string): Promise<StarterTemplate | null> => {
    try {
        const docRef = doc(starterTemplatesCollection, id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            return {
                id: docSnap.id,
                ...docSnap.data()
            } as StarterTemplate;
        } else {
            return null;
        }
    } catch (error) {
        console.error('Error getting starter template:', error);
        throw error;
    }
};

/**
 * Create a new starter template
 */
export const createStarterTemplate = async (templateData: Omit<StarterTemplate, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    try {
        console.log('Creating starter template with data:', templateData);

        // Handle icon upload if provided as base64
        let iconUrl = templateData.icon;
        if (iconUrl && iconUrl.startsWith('data:') && iconUrl.length > 50) {
            console.log('Uploading icon to Cloudinary...');
            try {
                iconUrl = await uploadBase64ImageToCloudinary(iconUrl, 'starter_template_icons');
            } catch (error) {
                console.error('Failed to upload icon:', error);
                // Continue without icon rather than failing the entire creation
                iconUrl = '';
            }
        } else {
            iconUrl = ''; // Clear any invalid icon data
        }

        // Handle cover image upload if provided as base64
        let coverImageUrl = templateData.coverImage;
        if (coverImageUrl && coverImageUrl.startsWith('data:') && coverImageUrl.length > 50) {
            console.log('Uploading cover image to Cloudinary...');
            try {
                coverImageUrl = await uploadBase64ImageToCloudinary(coverImageUrl, 'starter_template_covers');
            } catch (error) {
                console.error('Failed to upload cover image:', error);
                // Continue without cover image rather than failing the entire creation
                coverImageUrl = '';
            }
        } else {
            coverImageUrl = ''; // Clear any invalid cover image data
        }

        // Handle screenshots upload if provided as base64
        let screenshotUrls: string[] = [];
        if (templateData.screenshots && Array.isArray(templateData.screenshots) && templateData.screenshots.length > 0) {
            console.log(`Uploading ${templateData.screenshots.length} screenshots to Cloudinary...`);

            for (let i = 0; i < templateData.screenshots.length; i++) {
                const screenshot = templateData.screenshots[i];
                if (screenshot && screenshot.startsWith('data:') && screenshot.length > 50) {
                    try {
                        const uploadedUrl = await uploadBase64ImageToCloudinary(screenshot, 'starter_template_screenshots');
                        screenshotUrls.push(uploadedUrl);
                        console.log(`Screenshot ${i + 1} uploaded successfully`);
                    } catch (error) {
                        console.error(`Failed to upload screenshot ${i + 1}:`, error);
                        // Continue with other screenshots rather than failing the entire creation
                    }
                } else {
                    console.log(`Skipping invalid screenshot ${i + 1}`);
                }
            }
        }

        // Get current max order and add 1
        const templates = await getAllStarterTemplates();
        const maxOrder = templates.length > 0
            ? Math.max(...templates.map(template => template.order || 0))
            : -1;

        const docRef = await addDoc(starterTemplatesCollection, {
            ...templateData,
            icon: iconUrl,
            coverImage: coverImageUrl,
            screenshots: screenshotUrls,
            order: maxOrder + 1,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        });

        console.log('Starter template created successfully with ID:', docRef.id);
        return docRef.id;
    } catch (error) {
        console.error('Error creating starter template:', error);
        throw error;
    }
};

/**
 * Update a starter template
 */
export const updateStarterTemplate = async (id: string, templateData: Partial<Omit<StarterTemplate, 'id' | 'createdAt' | 'updatedAt'>>): Promise<void> => {
    try {
        console.log('Updating starter template with ID:', id, 'and data:', templateData);

        // Handle icon upload if provided as base64
        let iconUrl = templateData.icon;
        if (iconUrl && iconUrl.startsWith('data:') && iconUrl.length > 50) {
            console.log('Uploading new icon to Cloudinary...');
            try {
                iconUrl = await uploadBase64ImageToCloudinary(iconUrl, 'starter_template_icons');
            } catch (error) {
                console.error('Failed to upload icon:', error);
                // Keep existing icon rather than failing the entire update
                delete templateData.icon;
                iconUrl = undefined;
            }
        } else if (iconUrl && !iconUrl.startsWith('data:')) {
            // Keep existing URL
            iconUrl = templateData.icon;
        } else {
            // Clear invalid icon data
            iconUrl = '';
        }

        // Handle cover image upload if provided as base64
        let coverImageUrl = templateData.coverImage;
        if (coverImageUrl && coverImageUrl.startsWith('data:') && coverImageUrl.length > 50) {
            console.log('Uploading new cover image to Cloudinary...');
            try {
                coverImageUrl = await uploadBase64ImageToCloudinary(coverImageUrl, 'starter_template_covers');
            } catch (error) {
                console.error('Failed to upload cover image:', error);
                // Keep existing cover image rather than failing the entire update
                delete templateData.coverImage;
                coverImageUrl = undefined;
            }
        } else if (coverImageUrl && !coverImageUrl.startsWith('data:')) {
            // Keep existing URL
            coverImageUrl = templateData.coverImage;
        } else {
            // Clear invalid cover image data
            coverImageUrl = '';
        }

        // Handle screenshots upload if provided as base64
        let screenshotUrls = templateData.screenshots;
        if (screenshotUrls && Array.isArray(screenshotUrls) && screenshotUrls.length > 0) {
            console.log(`Processing ${screenshotUrls.length} screenshots...`);

            const processedScreenshots: string[] = [];
            for (let i = 0; i < screenshotUrls.length; i++) {
                const screenshot = screenshotUrls[i];
                if (screenshot && screenshot.startsWith('data:') && screenshot.length > 50) {
                    try {
                        const uploadedUrl = await uploadBase64ImageToCloudinary(screenshot, 'starter_template_screenshots');
                        processedScreenshots.push(uploadedUrl);
                        console.log(`Screenshot ${i + 1} uploaded successfully`);
                    } catch (error) {
                        console.error(`Failed to upload screenshot ${i + 1}:`, error);
                        // Continue with other screenshots rather than failing the entire update
                    }
                } else if (screenshot && !screenshot.startsWith('data:')) {
                    // Keep existing URL
                    processedScreenshots.push(screenshot);
                }
                // Skip empty or invalid screenshots
            }
            screenshotUrls = processedScreenshots;
        }

        const docRef = doc(starterTemplatesCollection, id);
        const updateData: any = {
            ...templateData,
            updatedAt: serverTimestamp()
        };

        // Only update image fields if they were processed
        if (iconUrl !== undefined) {
            updateData.icon = iconUrl;
        }
        if (coverImageUrl !== undefined) {
            updateData.coverImage = coverImageUrl;
        }
        if (screenshotUrls !== undefined) {
            updateData.screenshots = screenshotUrls;
        }

        await updateDoc(docRef, updateData);
        console.log('Starter template updated successfully');
    } catch (error) {
        console.error('Error updating starter template:', error);
        throw error;
    }
};

/**
 * Delete a starter template
 */
export const deleteStarterTemplate = async (id: string): Promise<void> => {
    try {
        const docRef = doc(starterTemplatesCollection, id);
        await deleteDoc(docRef);
    } catch (error) {
        console.error('Error deleting starter template:', error);
        throw error;
    }
};

/**
 * Update starter template order
 */
export const updateStarterTemplateOrder = async (id: string, newOrder: number): Promise<void> => {
    try {
        const docRef = doc(starterTemplatesCollection, id);
        await updateDoc(docRef, {
            order: newOrder,
            updatedAt: serverTimestamp()
        });
    } catch (error) {
        console.error('Error updating starter template order:', error);
        throw error;
    }
};

/**
 * Move starter template up in order
 */
export const moveStarterTemplateUp = async (id: string): Promise<void> => {
    try {
        const templates = await getAllStarterTemplates();
        const currentIndex = templates.findIndex(template => template.id === id);

        if (currentIndex > 0) {
            const currentTemplate = templates[currentIndex];
            const previousTemplate = templates[currentIndex - 1];

            // Swap orders
            if (currentTemplate.id && previousTemplate.id) {
                await updateStarterTemplateOrder(currentTemplate.id, previousTemplate.order || 0);
                await updateStarterTemplateOrder(previousTemplate.id, currentTemplate.order || 0);
            }
        }
    } catch (error) {
        console.error('Error moving starter template up:', error);
        throw error;
    }
};

/**
 * Move starter template down in order
 */
export const moveStarterTemplateDown = async (id: string): Promise<void> => {
    try {
        const templates = await getAllStarterTemplates();
        const currentIndex = templates.findIndex(template => template.id === id);

        if (currentIndex < templates.length - 1) {
            const currentTemplate = templates[currentIndex];
            const nextTemplate = templates[currentIndex + 1];

            // Swap orders
            if (currentTemplate.id && nextTemplate.id) {
                await updateStarterTemplateOrder(currentTemplate.id, nextTemplate.order || 0);
                await updateStarterTemplateOrder(nextTemplate.id, currentTemplate.order || 0);
            }
        }
    } catch (error) {
        console.error('Error moving starter template down:', error);
        throw error;
    }
}; 